import React, { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';

import AddIcon from '@mui/icons-material/Add';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import UserSearchWidget from '~/components/Common/UserSearchWidget';
import PromoFeature from '~/components/Promo/PromoFeature';

import { toPromoBrand } from '~/consts/promoBrand';

import { useBulkDeleteVoucherUsers } from '~/hooks/apis/promo/useBulkDeleteVoucherUsers';
import { useBulkUpdateUnusedVoucherUsersExpiry } from '~/hooks/apis/promo/useBulkUpdateUnusedVoucherUsersExpiry';
import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { allVoucherUsersQueryKeys } from '~/hooks/apis/promo/useListAllVoucherUsers';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import useCurrentUser from '~/hooks/useCurrentUser';
import usePromoFeatureAvailability from '~/hooks/usePromoFeatureAvailability';

import { listVoucherUsers } from '~/services/PromoService';

import { ConvertToCSV } from '~/utils/arrayUtils';
import { chunkArray } from '~/utils/chunkArray';
import { getDefaultAddVoucherUsersExpiryDate, isoToSydneyDate, normalizeToISODate } from '~/utils/dateFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import PageSubheader from '../Common/Elements/PageSubheader';
import DebugModal from '../DebugModal/DebugModal';

import BulkDeleteVoucherUsersDialog from './BulkDeleteVoucherUsersDialog';
import BulkUploadVoucherUsersDialog from './BulkUploadVoucherUsersDialog';
import VoucherUsersSearchPage from './VoucherUserLookup';

type Props = {
  userIds: Array<string>;
  onChange: (userIds: Array<string>) => void;
  expiredAt: string;
  onExpiredAtChange: (expiredAt: string) => void;
  /** Voucher form fields — used when resolving empty add-user expiry (same rules as initial default). */
  voucherUserExpiresAt?: string;
  voucherUserExpiresInDays?: string;
  disabled?: boolean;
  voucherId?: string;
  /** Exact voucher name for bulk expiry API (matches saved voucher name). */
  voucherName?: string;
  showAdvanceDescription?: boolean;
  bulkExpiryExpanded?: boolean;
  onBulkExpiryExpandedChange?: (expanded: boolean) => void;
  bulkExpiryFormRef?: React.RefObject<HTMLDivElement>;
  onBulkExpiryEntered?: () => void;
  /** Lifted user filter for promo requests when embedded lookup is on voucher edit (avoids duplicate Recent Requests). */
  promoRequestsUserFilter?: string;
  onPromoRequestsUserFilterChange?: (userId: string) => void;
};

type BulkExpiryUpdate = {
  expiredAt: string;
  title: string;
};

type BulkDeletePreview = {
  deletableUserIds: Array<string>;
  requestedCount: number;
  skippedCount: number;
};

const BULK_DELETE_VOUCHER_USER_BATCH_SIZE = 200;

export default function VoucherUsers({
  userIds,
  onChange,
  expiredAt,
  onExpiredAtChange,
  voucherUserExpiresAt = '',
  voucherUserExpiresInDays = '',
  showAdvanceDescription,
  bulkExpiryExpanded,
  onBulkExpiryExpandedChange,
  bulkExpiryFormRef,
  onBulkExpiryEntered,
  disabled = false,
  voucherId,
  voucherName = '',
  promoRequestsUserFilter,
  onPromoRequestsUserFilterChange,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const brand = useCurrentBrand();
  const promoBrand = toPromoBrand(brand);
  const { user: currentUser } = useCurrentUser();
  const canBulkAddVoucherUsers = usePromoFeatureAvailability('voucher-user-bulk-add');
  const canRemoveVoucherUsers = usePromoFeatureAvailability('voucher-user-bulk-remove');
  const canEditVoucherUsers = usePromoFeatureAvailability('voucher');
  const [lookupModeLocked, setLookupModeLocked] = useState(false);
  const [userMode, setUserMode] = useState('read-only');
  const isEditing =
    !disabled &&
    canEditVoucherUsers &&
    userMode !== 'read-only' &&
    (userMode !== 'bulk-edit' || canBulkAddVoucherUsers || canRemoveVoucherUsers);
  const isBulkEditing = isEditing && userMode === 'bulk-edit';
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchValue, setUserSearchValue] = useState('');
  const [allowVoucherReuse, setAllowVoucherReuse] = useState(false);
  const [reactivateExpiredVouchers, setReactivateExpiredVouchers] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isBulkDeleteCsvOpen, setIsBulkDeleteCsvOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [bulkExpiryOverrideDate, setBulkExpiryOverrideDate] = useState<string | null>(null);
  const [bulkExpiryUpdate, setBulkExpiryUpdate] = useState<BulkExpiryUpdate | null>(null);
  const [bulkDeletePreview, setBulkDeletePreview] = useState<BulkDeletePreview | null>(null);
  const [isPreparingBulkDelete, setIsPreparingBulkDelete] = useState(false);
  const [isDeletingAllVoucherUsers, setIsDeletingAllVoucherUsers] = useState(false);

  const voucherUsersPageDebugData = useMemo(
    () => ({
      voucherId,
      voucherName: voucherName.trim() || undefined,
      brand: brand ?? undefined,
    }),
    [brand, voucherId, voucherName],
  );

  const createVoucherUsersMutation = useCreateVoucherUsers();
  const bulkUpdateUnusedExpiryMutation = useBulkUpdateUnusedVoucherUsersExpiry();
  const bulkDeleteVoucherUsersMutation = useBulkDeleteVoucherUsers();

  const currentUserLabel = useMemo(() => {
    if (!currentUser) {
      return '';
    }
    return currentUser.email || currentUser.fullName || currentUser.id_member;
  }, [currentUser]);

  const defaultVoucherUserExpiryIso = useMemo(
    () => normalizeToISODate(getDefaultAddVoucherUsersExpiryDate({ voucherUserExpiresAt, voucherUserExpiresInDays })),
    [voucherUserExpiresAt, voucherUserExpiresInDays],
  );

  /** Add-user overrides do not affect the bulk update date. */
  const resolvedVoucherUserExpiryIso = useMemo(
    () => (expiredAt.trim() ? normalizeToISODate(expiredAt) : defaultVoucherUserExpiryIso),
    [defaultVoucherUserExpiryIso, expiredAt],
  );

  const resolvedBulkExpiryIso =
    bulkExpiryOverrideDate === null
      ? defaultVoucherUserExpiryIso
      : bulkExpiryOverrideDate
        ? normalizeToISODate(bulkExpiryOverrideDate)
        : '';
  const bulkExpiryDisplay = bulkExpiryOverrideDate ?? isoToSydneyDate(defaultVoucherUserExpiryIso);

  const handleAdd = useCallback(
    async (userIdToAdd = selectedUserId) => {
      if (!userIdToAdd) {
        enqueueSnackbar('Select a user to add', { variant: 'error' });
        return;
      }

      if (!voucherId) {
        onChange([...userIds, userIdToAdd]);
        setSelectedUserId('');
        setUserSearchValue('');
        return;
      }

      try {
        const response = await createVoucherUsersMutation.mutateAsync({
          voucher_id: voucherId,
          expired_at: resolvedVoucherUserExpiryIso,
          allow_voucher_reuse: allowVoucherReuse,
          reactivate_expired_vouchers: reactivateExpiredVouchers,
          voucher_users: [{ user_id: userIdToAdd }],
        });
        const r = response.result;
        const resultWide = r as {
          createdVoucherUsers?: Array<unknown>;
          updatedVoucherUsers?: Array<unknown>;
          reactivatedVoucherUsers?: Array<unknown>;
          skippedVoucherUsers?: Array<unknown>;
          voucherUsers?: Array<unknown>;
        };
        const anySuccess =
          (resultWide.createdVoucherUsers?.length ?? 0) > 0 ||
          (resultWide.updatedVoucherUsers?.length ?? 0) > 0 ||
          (resultWide.reactivatedVoucherUsers?.length ?? 0) > 0 ||
          (resultWide.voucherUsers?.length ?? 0) > 0;
        const anySkipped = (resultWide.skippedVoucherUsers?.length ?? 0) > 0;
        const shouldClearForm = anySuccess || !anySkipped;

        if (r?.skippedVoucherUsers?.length) {
          const reason = r.skippedVoucherUsers[0].reason === 'already_redeemed' ? 'already redeemed' : 'expired unused';
          enqueueSnackbar(`User was skipped — ${reason}`, { variant: 'warning' });
        } else if (r?.reactivatedVoucherUsers?.length) {
          enqueueSnackbar('Reactivated expired voucher user', { variant: 'success' });
        } else if (r?.updatedVoucherUsers?.length) {
          enqueueSnackbar('Updated existing voucher user (reuse)', { variant: 'success' });
        } else {
          enqueueSnackbar('Added voucher user', { variant: 'success' });
        }
        queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
        queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
        if (shouldClearForm) {
          setSelectedUserId('');
          setUserSearchValue('');
        }
      } catch (error) {
        enqueueSnackbar(`Failed to add voucher user: ${(error as Error).message}`, { variant: 'error' });
      }
    },
    [
      allowVoucherReuse,
      createVoucherUsersMutation,
      enqueueSnackbar,
      onChange,
      queryClient,
      reactivateExpiredVouchers,
      selectedUserId,
      userIds,
      voucherId,
      resolvedVoucherUserExpiryIso,
    ],
  );

  const handleAssignCurrentUser = useCallback(() => {
    if (!currentUser?.id_member) {
      enqueueSnackbar('Current user could not be resolved', { variant: 'error' });
      return;
    }
    setSelectedUserId(currentUser.id_member);
    setUserSearchValue(currentUserLabel);
    handleAdd(currentUser.id_member);
  }, [currentUser?.id_member, currentUserLabel, enqueueSnackbar, handleAdd]);

  // Handle form submit (Enter key) to add user when a user is selected
  const handleFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (selectedUserId && !disabled && !createVoucherUsersMutation.isPending) {
        handleAdd();
      }
    },
    [selectedUserId, disabled, createVoucherUsersMutation.isPending, handleAdd],
  );

  const handleDownloadCsv = useCallback(async () => {
    if (!voucherId) {
      return;
    }

    setIsDownloading(true);
    try {
      const PAGE_SIZE = 200;
      let page = 0;
      let allUsers: Array<definitions['VoucherUser']> = [];
      let hasMore = true;

      while (hasMore) {
        const response = await listVoucherUsers(voucherId, {
          page,
          limit: PAGE_SIZE,
          includeExpired: true,
          includeUsed: true,
          ...(promoBrand ? { brand: promoBrand } : {}),
        });
        const users = response.result?.voucherUsers ?? [];
        allUsers = allUsers.concat(users);
        const total = response.result?.total ?? 0;
        hasMore = users.length > 0 && allUsers.length < total;
        page += 1;
      }

      const csvData = allUsers.map((user) => ({
        user_id: user.userId,
        created_at: user.createdAt ?? '',
        used_at: user.usedAt ?? '',
        expired_at: user.expiredAt ?? '',
      }));

      const csv = ConvertToCSV(csvData);
      fileDownload(csv, `voucher-users-${voucherId}.csv`);
      enqueueSnackbar(`Downloaded ${allUsers.length} voucher users`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to download voucher users: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  }, [promoBrand, voucherId, enqueueSnackbar]);

  const handleBulkExpiryOpen = useCallback(
    (update: BulkExpiryUpdate) => {
      const trimmedName = voucherName.trim();
      if (!trimmedName) {
        enqueueSnackbar('Set a voucher name before bulk-updating expiry', { variant: 'error' });
        return;
      }
      if (!brand) {
        enqueueSnackbar('Select a brand (tenant) before bulk-updating expiry', { variant: 'error' });
        return;
      }
      setBulkExpiryUpdate(update);
    },
    [brand, enqueueSnackbar, voucherName],
  );

  const handleBulkExpiryConfirm = useCallback(async () => {
    const trimmedName = voucherName.trim();
    if (!trimmedName || !voucherId || !brand || !bulkExpiryUpdate) {
      setBulkExpiryUpdate(null);
      return;
    }
    try {
      const response = await bulkUpdateUnusedExpiryMutation.mutateAsync({
        voucherName: trimmedName,
        expiredAt: bulkExpiryUpdate.expiredAt,
        brand: brand as definitions['Promo Brands'],
      });
      const n = response.result?.updatedCount ?? 0;
      enqueueSnackbar(response.message || `Updated ${n} unused voucher user row(s)`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
      queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
    } catch (error) {
      enqueueSnackbar(`Bulk update failed: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setBulkExpiryUpdate(null);
    }
  }, [bulkUpdateUnusedExpiryMutation, brand, enqueueSnackbar, queryClient, bulkExpiryUpdate, voucherId, voucherName]);

  const handleBulkExpiryDialogClose = useCallback(
    (_event: object, _reason: 'backdropClick' | 'escapeKeyDown') => {
      if (bulkUpdateUnusedExpiryMutation.isPending) {
        return;
      }
      setBulkExpiryUpdate(null);
    },
    [bulkUpdateUnusedExpiryMutation.isPending],
  );

  const loadAllVoucherUserIds = useCallback(async () => {
    if (!voucherId) {
      return [];
    }

    const userIds = new Set<string>();
    let page = 0;
    let fetchedCount = 0;
    let hasMore = true;

    while (hasMore) {
      const response = await listVoucherUsers(voucherId, {
        page,
        limit: BULK_DELETE_VOUCHER_USER_BATCH_SIZE,
        includeExpired: true,
        includeUsed: true,
        ...(promoBrand ? { brand: promoBrand } : {}),
      });
      const users = response.result?.voucherUsers ?? [];
      users.forEach((user) => userIds.add(user.userId));
      fetchedCount += users.length;
      hasMore = users.length > 0 && fetchedCount < (response.result?.total ?? 0);
      page += 1;
    }

    return Array.from(userIds);
  }, [promoBrand, voucherId]);

  const refreshVoucherUserQueries = useCallback(async () => {
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] }),
        queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] }),
      ]);
    } catch (error) {
      enqueueSnackbar(`Voucher users were deleted, but the page could not refresh: ${(error as Error).message}`, {
        variant: 'warning',
      });
    }
  }, [enqueueSnackbar, queryClient, voucherId]);

  const handleBulkDeletePreview = useCallback(async () => {
    if (!voucherId || !canRemoveVoucherUsers) {
      return;
    }

    setIsPreparingBulkDelete(true);
    try {
      const userIds = await loadAllVoucherUserIds();
      if (userIds.length === 0) {
        enqueueSnackbar('There are no voucher users to delete', { variant: 'info' });
        return;
      }

      const results = [];
      for (const batch of chunkArray(userIds, BULK_DELETE_VOUCHER_USER_BATCH_SIZE)) {
        results.push(
          await bulkDeleteVoucherUsersMutation.mutateAsync({
            voucher_id: voucherId,
            voucher_users: batch.map((user_id) => ({ user_id })),
            dryRun: true,
          }),
        );
      }

      setBulkDeletePreview({
        deletableUserIds: results.flatMap((response) => response.result.willDelete.map((user) => user.userId)),
        skippedCount: results.reduce((count, response) => count + response.result.skipped.length, 0),
        requestedCount: userIds.length,
      });
    } catch (error) {
      enqueueSnackbar(`Could not preview voucher user deletion: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setIsPreparingBulkDelete(false);
    }
  }, [bulkDeleteVoucherUsersMutation, canRemoveVoucherUsers, enqueueSnackbar, loadAllVoucherUserIds, voucherId]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (!voucherId || !bulkDeletePreview || !canRemoveVoucherUsers) {
      setBulkDeletePreview(null);
      return;
    }

    setIsDeletingAllVoucherUsers(true);
    let deletedCount = 0;
    try {
      for (const batch of chunkArray(bulkDeletePreview.deletableUserIds, BULK_DELETE_VOUCHER_USER_BATCH_SIZE)) {
        const response = await bulkDeleteVoucherUsersMutation.mutateAsync({
          voucher_id: voucherId,
          voucher_users: batch.map((user_id) => ({ user_id })),
          dryRun: false,
        });
        deletedCount += response.result.deletedVoucherUsers.length;
      }
      enqueueSnackbar(`Deleted ${deletedCount} voucher user${deletedCount === 1 ? '' : 's'}`, { variant: 'success' });
      setBulkDeletePreview(null);
      await refreshVoucherUserQueries();
    } catch (error) {
      enqueueSnackbar(
        `Deletion stopped after deleting ${deletedCount} voucher user${deletedCount === 1 ? '' : 's'}: ${(error as Error).message}`,
        { variant: 'error' },
      );
      setBulkDeletePreview(null);
      await refreshVoucherUserQueries();
    } finally {
      setIsDeletingAllVoucherUsers(false);
    }
  }, [
    bulkDeletePreview,
    bulkDeleteVoucherUsersMutation,
    canRemoveVoucherUsers,
    enqueueSnackbar,
    refreshVoucherUserQueries,
    voucherId,
  ]);

  const isBulkDeleteAllBusy = isPreparingBulkDelete || isDeletingAllVoucherUsers;

  const handleBulkDeleteDialogClose = useCallback(
    (_event: object, _reason: 'backdropClick' | 'escapeKeyDown') => {
      if (!isBulkDeleteAllBusy) {
        setBulkDeletePreview(null);
      }
    },
    [isBulkDeleteAllBusy],
  );

  return (
    <Box id="users">
      <PageSubheader title="Users">
        {showAdvanceDescription && (
          <Box component="span" color="text.secondary" alignItems="left">
            Manage the users assigned to this voucher and their expiry dates.
          </Box>
        )}
      </PageSubheader>
      <PromoFeature
        presentation="radioSelect"
        label="User mode"
        disabled={lookupModeLocked}
        disabledReason="Finish the current voucher user operation before changing modes."
        value={isEditing ? userMode : 'read-only'}
        onChange={setUserMode}
        options={[
          { value: 'read-only', label: 'Read-only' },
          {
            value: 'edit',
            label: 'Edit',
            disabled,
            disabledReason: 'Wait until the voucher has finished saving.',
            featureKey: 'voucher',
          },
          {
            value: 'bulk-edit',
            label: 'Bulk-edit',
            disabled,
            disabledReason: 'Wait until the voucher has finished saving.',
            featureKeys: ['voucher-user-bulk-add', 'voucher-user-bulk-remove'],
          },
        ]}
      />
      {!voucherId ? (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Create the voucher to add users.
        </Typography>
      ) : isEditing ? (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Add Voucher Users
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            User expiry
          </Typography>
          <RadioGroup
            row
            value={expiredAt.trim() ? 'date' : 'voucher'}
            onChange={(e) => {
              const next = e.target.value;
              if (next === 'voucher') {
                onExpiredAtChange('');
              } else {
                onExpiredAtChange(
                  getDefaultAddVoucherUsersExpiryDate({
                    voucherUserExpiresAt,
                    voucherUserExpiresInDays,
                  }),
                );
              }
            }}
            sx={{ mb: 1 }}
          >
            <FormControlLabel value="voucher" control={<Radio />} label="Voucher Default" disabled={disabled} />
            <FormControlLabel value="date" control={<Radio />} label="Expiry Override" disabled={disabled} />
          </RadioGroup>
          {expiredAt.trim() ? (
            <TextField
              label="Expires At"
              type="date"
              value={expiredAt}
              onChange={(event) => onExpiredAtChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
              sx={{ minWidth: 240, mb: 2 }}
              helperText={
                <Box>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Override expiry for users added below.
                  </Typography>
                  <DateFormatterDetailed date={normalizeToISODate(expiredAt)} highlightIfPast includeYear />
                </Box>
              }
            />
          ) : (
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Uses voucher expiry settings — currently
              </Typography>
              <DateFormatterDetailed date={resolvedVoucherUserExpiryIso} highlightIfPast includeYear />
              <Typography variant="body2" color="text.secondary">
                when adding users.
              </Typography>
            </Box>
          )}
          <form onSubmit={handleFormSubmit}>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Box sx={{ flex: 1 }}>
                <UserSearchWidget
                  id="voucher-user-search"
                  label="Name / Email / Customer Support Code / User ID"
                  formErrors={[]}
                  onChange={(_, value) => setSelectedUserId(value)}
                  passedAutocompleteValue={userSearchValue}
                  setPassedAutocompleteValue={setUserSearchValue}
                  disabled={disabled}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={disabled || createVoucherUsersMutation.isPending}
              >
                Add users
              </Button>
              <Button
                type="button"
                variant="text"
                color="primary"
                startIcon={<AssignmentIndIcon />}
                onClick={handleAssignCurrentUser}
                disabled={disabled || createVoucherUsersMutation.isPending || !currentUser?.id_member}
              >
                Assign to me
              </Button>
            </Stack>
          </form>
          <Stack direction="row" spacing={2} mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowVoucherReuse}
                  onChange={(e) => setAllowVoucherReuse(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Allow voucher reuse"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={reactivateExpiredVouchers}
                  onChange={(e) => setReactivateExpiredVouchers(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Reactivate expired vouchers"
            />
          </Stack>
        </>
      ) : null}

      {voucherId && (
        <Box mb={3} mt={3}>
          <VoucherUsersSearchPage
            userMode={isEditing ? userMode : 'read-only'}
            onModeLockChange={setLookupModeLocked}
            voucherId={voucherId}
            defaultIncludeExpired
            hideFooterDebug
            showExpireVoucherUserActions={isEditing}
            expireVoucherUserActionsDisabled={!isEditing}
            promoRequestsUserFilter={promoRequestsUserFilter}
            onPromoRequestsUserFilterChange={onPromoRequestsUserFilterChange}
          />
          {isBulkEditing && (
            <Accordion
              defaultExpanded={false}
              expanded={bulkExpiryExpanded}
              onChange={(_, expanded) => onBulkExpiryExpandedChange?.(expanded)}
              slotProps={{ transition: { onEntered: onBulkExpiryEntered } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={500}>Bulk voucher user operations</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack direction="row" spacing={2} mb={0.5}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => setIsBulkUploadOpen(true)}
                    disabled={disabled || !canBulkAddVoucherUsers}
                  >
                    Bulk Upload CSV
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadCsv}
                    disabled={isDownloading}
                  >
                    {isDownloading ? 'Downloading...' : 'Download CSV'}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Exports every voucher user row for this voucher (not limited to the table filters).
                </Typography>
                <Box
                  ref={bulkExpiryFormRef}
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 2,
                  })}
                >
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle1">Bulk update assigned voucher users</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Update the expiry date for all unused voucher users assigned to this voucher.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                      <TextField
                        label="Expiry applied"
                        type="date"
                        value={bulkExpiryDisplay}
                        onChange={(event) => setBulkExpiryOverrideDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={disabled || !voucherName.trim() || !brand}
                        sx={{ minWidth: 240 }}
                        helperText={
                          <Box>
                            <Typography variant="caption" color="text.secondary" component="div">
                              Set a date for this bulk update, or use the voucher default.
                            </Typography>
                            {resolvedBulkExpiryIso && (
                              <DateFormatterDetailed date={resolvedBulkExpiryIso} highlightIfPast includeYear />
                            )}
                          </Box>
                        }
                      />
                      <Button
                        variant="text"
                        onClick={() => setBulkExpiryOverrideDate(null)}
                        disabled={disabled || bulkExpiryOverrideDate === null}
                      >
                        Use voucher default
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<EditCalendarIcon />}
                        onClick={() =>
                          handleBulkExpiryOpen({
                            expiredAt: resolvedBulkExpiryIso,
                            title: 'Bulk update assigned voucher users',
                          })
                        }
                        disabled={
                          disabled ||
                          bulkUpdateUnusedExpiryMutation.isPending ||
                          !resolvedBulkExpiryIso ||
                          !voucherName.trim() ||
                          !brand
                        }
                      >
                        Update all user expiry dates
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
                <Accordion defaultExpanded={false} sx={{ mt: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="voucher-users-danger-zone-content"
                    id="voucher-users-danger-zone-header"
                  >
                    <Typography color="error.main" fontWeight={600}>
                      Danger Zone
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={(theme) => ({
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 1,
                        mt: 2,
                        p: 2,
                      })}
                    >
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle1">Expire active voucher users</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Set the expiry for all active voucher users to one minute from now. Used voucher users are
                            not changed.
                          </Typography>
                        </Box>
                        <Box>
                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<EditCalendarIcon />}
                            onClick={() =>
                              handleBulkExpiryOpen({
                                expiredAt: new Date(Date.now() + 60 * 1000).toISOString(),
                                title: 'Expire all active voucher users',
                              })
                            }
                            disabled={
                              disabled || bulkUpdateUnusedExpiryMutation.isPending || !voucherName.trim() || !brand
                            }
                          >
                            Expire all active voucher users
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                    <Box
                      sx={(theme) => ({
                        border: `1px solid ${theme.palette.error.light}`,
                        borderRadius: 1,
                        mt: 2,
                        p: 2,
                      })}
                    >
                      <PromoFeature featureKey="voucher-user-bulk-remove" presentation="raw">
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              These actions permanently delete unused voucher users, including expired users.
                            </Typography>
                          </Box>
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="subtitle2">Delete users from CSV</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Review a selected list of users before deleting them.
                              </Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<UploadIcon />}
                              onClick={() => setIsBulkDeleteCsvOpen(true)}
                              disabled={disabled || !canRemoveVoucherUsers || isBulkDeleteAllBusy}
                            >
                              Bulk delete from CSV
                            </Button>
                          </Stack>
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="subtitle2">Delete all voucher users</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Review every eligible user assigned to this voucher before deleting them all.
                              </Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={handleBulkDeletePreview}
                              disabled={disabled || !canRemoveVoucherUsers || isBulkDeleteAllBusy}
                            >
                              {isPreparingBulkDelete ? 'Preparing deletion...' : 'Delete all voucher users'}
                            </Button>
                          </Stack>
                        </Stack>
                      </PromoFeature>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      <Dialog
        open={bulkExpiryUpdate !== null}
        onClose={handleBulkExpiryDialogClose}
        disableEscapeKeyDown={bulkUpdateUnusedExpiryMutation.isPending}
      >
        <DialogTitle>{bulkExpiryUpdate?.title}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will update the expiry for every unused voucher user row that matches voucher name &quot;
            {voucherName.trim() || '—'}&quot;. Redeemed rows are not changed.
          </Alert>
          <Typography variant="body2" component="div" gutterBottom>
            Set expiry to:
          </Typography>
          {bulkExpiryUpdate?.expiredAt ? (
            <Box sx={{ mb: 1 }}>
              <DateFormatterDetailed date={bulkExpiryUpdate.expiredAt} highlightIfPast includeYear />
            </Box>
          ) : (
            <Typography variant="body2" component="div" gutterBottom>
              —
            </Typography>
          )}
          <Typography variant="body2" component="div">
            The selected date will be applied at UTC midnight when entered as a date only.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setBulkExpiryUpdate(null)}
            disabled={bulkUpdateUnusedExpiryMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkExpiryConfirm}
            disabled={bulkUpdateUnusedExpiryMutation.isPending}
          >
            Update expiry
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeletePreview !== null}
        onClose={handleBulkDeleteDialogClose}
        disableEscapeKeyDown={isBulkDeleteAllBusy}
      >
        <DialogTitle>Delete all voucher users</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {`This will permanently delete ${bulkDeletePreview?.deletableUserIds.length ?? 0} of ${bulkDeletePreview?.requestedCount ?? 0} voucher user${bulkDeletePreview?.requestedCount === 1 ? '' : 's'}. ${bulkDeletePreview?.skippedCount ?? 0} cannot be deleted because they have already been redeemed, have an active voucher log, or are no longer assigned.`}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setBulkDeletePreview(null)} disabled={isBulkDeleteAllBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDeleteConfirm}
            disabled={
              !canRemoveVoucherUsers || isBulkDeleteAllBusy || (bulkDeletePreview?.deletableUserIds.length ?? 0) === 0
            }
          >
            {isDeletingAllVoucherUsers ? 'Deleting...' : 'Confirm deletion'}
          </Button>
        </DialogActions>
      </Dialog>

      {voucherId && canBulkAddVoucherUsers && (
        <BulkUploadVoucherUsersDialog
          open={isBulkUploadOpen}
          onClose={() => setIsBulkUploadOpen(false)}
          voucherId={voucherId}
          brand={brand}
        />
      )}
      {voucherId && canRemoveVoucherUsers && (
        <BulkDeleteVoucherUsersDialog
          open={isBulkDeleteCsvOpen}
          onClose={() => setIsBulkDeleteCsvOpen(false)}
          voucherId={voucherId}
          voucherName={voucherName}
        />
      )}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <DebugModal type="generic" title="Voucher users page debug" data={voucherUsersPageDebugData} />
      </Box>
    </Box>
  );
}
